<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="68px"
    >
      <!-- ★ 原型是「关键字/申请人/单号」一个输入框的多字段模糊（后端 keyword 匹配
           合同编号 / 合同名称 / 订单编号 / 项目名称 / 备注），取代了原来的「合同编号」+「合同名称」两个框。 -->
      <el-form-item label="关键字" prop="keyword">
        <el-input
          v-model="queryParams.keyword"
          class="!w-240px"
          clearable
          placeholder="关键字 / 申请人 / 单号"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <!-- ★「签约月份」绑的是 sign_date（签约日期），不是 order_date（下单日期）。 -->
      <el-form-item label="签约月份" prop="signMonth">
        <el-date-picker
          v-model="queryParams.signMonth"
          class="!w-240px"
          clearable
          placeholder="选择签约月份"
          type="month"
          value-format="YYYY-MM"
          @change="handleQuery"
        />
      </el-form-item>
      <el-form-item label="客户" prop="customerId">
        <el-select
          v-model="queryParams.customerId"
          class="!w-240px"
          clearable
          placeholder="请选择客户"
          @keyup.enter="handleQuery"
        >
          <el-option
            v-for="item in customerList"
            :key="item.id"
            :label="item.name"
            :value="item.id!"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button v-hasPermi="['crm:contract:create']" type="primary" @click="openForm('create')">
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['crm:contract:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表：全部合同（不传 sceneType ⇒ 后端不做数据权限过滤 ⇒ 全量） -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column align="center" fixed="left" label="合同编号" prop="no" width="180" />
      <!-- 2026-09-21 按原型补充：订单编号（当前是手填的占位字段，订单与合同尚未打通） -->
      <el-table-column align="center" label="订单编号" prop="orderNo" width="120" />
      <el-table-column align="center" fixed="left" label="合同名称" prop="name" width="160">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.id)">
            {{ scope.row.name }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="客户名称" prop="customerName" width="120">
        <template #default="scope">
          <el-link
            :underline="false"
            type="primary"
            @click="openCustomerDetail(scope.row.customerId)"
          >
            {{ scope.row.customerName }}
          </el-link>
        </template>
      </el-table-column>
      <!-- 2026-09-21 按原型补充：合同类型 / 签约日期 -->
      <el-table-column align="center" label="合同类型" prop="contractType" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_CONTRACT_TYPE" :value="scope.row.contractType" />
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        label="签约日期"
        prop="signDate"
        width="120"
        :formatter="dateFormatter2"
      />
      <el-table-column align="center" label="商机名称" prop="businessName" width="130">
        <template #default="scope">
          <el-link
            :underline="false"
            type="primary"
            @click="openBusinessDetail(scope.row.businessId)"
          >
            {{ scope.row.businessName }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        label="合同金额（元）"
        prop="totalPrice"
        width="140"
        :formatter="erpPriceTableColumnFormatter"
      />
      <!-- 2026-09-21 按原型补充：付款方式 / 收付款 -->
      <el-table-column align="center" label="付款方式" prop="paymentMethod" width="100">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_CONTRACT_PAYMENT_METHOD"
            :value="scope.row.paymentMethod"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" label="收/付款" prop="paymentDirection" width="100">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_CONTRACT_PAYMENT_DIRECTION"
            :value="scope.row.paymentDirection"
          />
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        label="下单时间"
        prop="orderDate"
        width="120"
        :formatter="dateFormatter2"
      />
      <el-table-column
        align="center"
        label="合同开始时间"
        prop="startTime"
        width="120"
        :formatter="dateFormatter2"
      />
      <!-- 原型叫「到期日期」；字段仍是 end_time，只是改了 label（2026-09-21） -->
      <el-table-column
        align="center"
        label="到期日期"
        prop="endTime"
        width="120"
        :formatter="dateFormatter2"
      />
      <el-table-column align="center" label="客户签约人" prop="signContactName" width="130">
        <template #default="scope">
          <el-link
            :underline="false"
            type="primary"
            @click="openContactDetail(scope.row.signContactId)"
          >
            {{ scope.row.signContactName }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="公司签约人" prop="signUserName" width="130" />
      <el-table-column align="center" label="备注" prop="remark" width="200" />
      <!-- 2026-09-21 按原型补充：说明（与「备注」remark 语义不同，两者并存） -->
      <el-table-column align="center" label="说明" prop="description" width="200" />
      <el-table-column
        align="center"
        label="已回款金额（元）"
        prop="totalReceivablePrice"
        width="140"
        :formatter="erpPriceTableColumnFormatter"
      />
      <!-- ★ 原型把「已回款 / 未回款」两列合并成 1 列「待收/付金额」，带方向前缀。
           收款单 ⇒「待收」= 合同金额 − 已回款；付款单 ⇒「待付」= 合同金额（暂无付款记录来源）。 -->
      <el-table-column align="center" label="待收/付金额（元）" width="150">
        <template #default="scope">
          {{ pendingAmountLabel(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="最后跟进时间"
        prop="contactLastTime"
        width="180px"
      />
      <el-table-column align="center" label="负责人" prop="ownerUserName" width="120" />
      <el-table-column align="center" label="所属部门" prop="ownerUserDeptName" width="100px" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="更新时间"
        prop="updateTime"
        width="180px"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180px"
      />
      <el-table-column align="center" label="创建人" prop="creatorName" width="120" />
      <!-- 2026-09-21 按原型补充：申请人部门。
           ⚠️ 是**申请人（creator）**的部门，与上面的「所属部门」= 负责人（ownerUserId）的部门口径不同。 -->
      <el-table-column align="center" label="申请人部门" prop="creatorDeptName" width="110px" />
      <!-- ★ 「合同状态」列（即审批状态 auditStatus）按需求隐藏（2026-09-21）：审批入口已下线，
           该列恒为「未提交」。恢复：取消注释即可。 -->
      <!-- <el-table-column align="center" fixed="right" label="合同状态" prop="auditStatus" width="120">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_AUDIT_STATUS" :value="scope.row.auditStatus" />
        </template>
      </el-table-column> -->
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="250"
      >
        <template #default="scope">
          <el-button
            v-if="scope.row.auditStatus === 0"
            v-hasPermi="['crm:contract:update']"
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
          >
            编辑
          </el-button>
          <!-- ★ 「提交审核 / 查看审批」按需求隐藏（2026-09-21）：合同不走审批流，审批入口整体下线。
               （后端 BPM 流程定义 crm-contract-audit 本就不存在，点提交必然失败。）
               恢复：把下面两段注释都取消即可；两段原本是 v-if / v-else 互斥对，
               请给「查看审批」那段补回 v-else。handleSubmit / handleProcessDetail
               两个方法都保留未删，恢复后可直接使用。 -->
          <!-- <el-button
            v-if="scope.row.auditStatus === 0"
            v-hasPermi="['crm:contract:update']"
            link
            type="primary"
            @click="handleSubmit(scope.row)"
          >
            提交审核
          </el-button> -->
          <!-- <el-button
            v-if="scope.row.auditStatus !== 0"
            v-hasPermi="['crm:contract:update']"
            link
            type="primary"
            @click="handleProcessDetail(scope.row)"
          >
            查看审批
          </el-button> -->
          <el-button
            v-hasPermi="['crm:contract:query']"
            link
            type="primary"
            @click="openDetail(scope.row.id)"
          >
            详情
          </el-button>
          <el-button
            v-hasPermi="['crm:contract:delete']"
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <ContractForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import { dateFormatter, dateFormatter2 } from '@/utils/formatTime'
import download from '@/utils/download'
import * as ContractApi from '@/api/crm/contract'
import ContractForm from '../ContractForm.vue'
import { DICT_TYPE } from '@/utils/dict'
import { erpPriceInputFormatter, erpPriceTableColumnFormatter } from '@/utils'
import * as CustomerApi from '@/api/crm/customer'

defineOptions({ name: 'CrmContractAll' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref([]) // 列表的数据
/**
 * ★ 这里**故意不声明 sceneType**：qs.stringify 会丢掉 undefined 的键，
 *   后端 CrmContractPageReqVO#sceneType 收到 null ⇒ CrmPermissionUtils 不拼任何权限条件 ⇒ 全量。
 *   一旦补上 sceneType 初值，本页就退化成「我的合同」，请勿添加。
 */
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  keyword: undefined as string | undefined,
  signMonth: undefined as string | undefined,
  customerId: undefined as number | undefined
})
const queryFormRef = ref() // 搜索的表单
const exportLoading = ref(false) // 导出的加载中
const customerList = ref<CustomerApi.CustomerVO[]>([]) // 客户列表

/**
 * 组装真正发给后端的查询参数。
 *
 * 「签约月份」在 UI 上是 `type="month"`（值为 `YYYY-MM`），后端要的是
 * `signDates` 闭区间 ⇒ 这里换算成本月首日 00:00:00 ~ 本月末日 23:59:59。
 * 同时把 UI 专用的 `signMonth` 从参数里摘掉（后端没有这个字段）。
 */
const buildQueryParams = () => {
  const params: Record<string, any> = { ...queryParams }
  delete params.signMonth
  if (queryParams.signMonth) {
    const monthStart = dayjs(queryParams.signMonth + '-01')
    params.signDates = [
      monthStart.startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      monthStart.endOf('month').format('YYYY-MM-DD HH:mm:ss')
    ]
  }
  return params
}

/** 「待收/付金额」列：按收付款方向加前缀 */
const pendingAmountLabel = (row: ContractApi.ContractVO) => {
  // 付款单没有付款记录来源，「待付」只能取合同金额，语义是「应付总额」
  if (row.paymentDirection === 2) {
    return '待付' + erpPriceInputFormatter(row.totalPrice ?? 0)
  }
  return '待收' + erpPriceInputFormatter((row.totalPrice ?? 0) - (row.totalReceivablePrice ?? 0))
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await ContractApi.getContractPage(buildQueryParams())
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作（只清业务条件，不涉及口径参数） */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.keyword = undefined
  queryParams.signMonth = undefined
  queryParams.customerId = undefined
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref<InstanceType<typeof ContractForm>>()
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await ContractApi.deleteContract(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await ContractApi.exportContract(buildQueryParams())
    download.excel(data, '合同.xls')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 提交审核 **/
const handleSubmit = async (row: ContractApi.ContractVO) => {
  await message.confirm(`您确定提交【${row.name}】审核吗？`)
  await ContractApi.submitContract(row.id)
  message.success('提交审核成功！')
  await getList()
}

/** 查看审批 */
const handleProcessDetail = (row: ContractApi.ContractVO) => {
  push({ name: 'BpmProcessInstanceDetail', query: { id: row.processInstanceId } })
}

/** 打开各详情 */
const { push } = useRouter()
const openDetail = (id: number) => {
  push({ name: 'CrmContractDetail', params: { id } })
}

const openCustomerDetail = (id: number) => {
  push({ name: 'CrmCustomerDetail', params: { id } })
}

const openContactDetail = (id: number) => {
  push({ name: 'CrmContactDetail', params: { id } })
}

const openBusinessDetail = (id: number) => {
  push({ name: 'CrmBusinessDetail', params: { id } })
}

/** 初始化 **/
onMounted(async () => {
  await getList()
  customerList.value = await CustomerApi.getCustomerSimpleList()
})
</script>
